import React, { useState, useEffect } from 'react';
import { apiService } from '../services/api';

const CharityCard = ({ charity }) => {
  const {
    name,
    mission,
    logo,
    website,
    description,
    amountRaised,
    lastEvent
  } = charity;

  return (
    <div className="card hover-lift">
      <div className="flex items-start space-x-4 mb-4">
        <div className="flex-shrink-0">
          {logo ? (
            <img
              src={logo}
              alt={`${name} logo`}
              className="w-16 h-16 rounded-lg object-cover"
            />
          ) : (
            <div className="w-16 h-16 bg-gold-200 rounded-lg flex items-center justify-center">
              <span className="text-2xl">💛</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-forest-800 mb-1">
            {name}
          </h3>
          <p className="text-sm text-forest-600 italic mb-2">
            "{mission}"
          </p>
          {amountRaised && (
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gold-100 text-gold-800 mb-2">
              <span className="font-semibold">${amountRaised.toLocaleString()} raised</span>
            </div>
          )}
        </div>
      </div>

      <p className="text-forest-700 mb-4">
        {description}
      </p>

      {lastEvent && (
        <div className="bg-forest-50 rounded-lg p-3 mb-4">
          <p className="text-sm text-forest-600">
            <span className="font-medium">Last supported:</span> {lastEvent}
          </p>
        </div>
      )}

      <div style={{display: 'flex', gap: '0.75rem', flexWrap: 'wrap', paddingTop: '0.5rem'}}>
        <a
          href={website}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-enhanced btn-primary"
        >
          Learn More
        </a>
        <button className="btn-enhanced btn-outline">
          Donate
        </button>
      </div>
    </div>
  );
};


const Charity = () => {
  const [charities, setCharities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statisticsData, setStatisticsData] = useState({
    charity: { donationLink: '' }
  });
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    fetchCharityData();
    fetchStatisticsData();
    fetchCharityEvents();
  }, []);

  const fetchCharityData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getCharityData();
      setCharities(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching charity data:', error);
      setError('Failed to load charity information. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStatisticsData = async () => {
    try {
      const response = await apiService.getStatisticsData();
      if (response.data) {
        setStatisticsData(response.data);
      }
    } catch (error) {
      console.warn('Could not fetch statistics data:', error);
    }
  };

  const fetchCharityEvents = async () => {
    try {
      const response = await apiService.getEventsData();
      if (response.data && Array.isArray(response.data)) {
        // Filter events that have 'charity' tag and are upcoming
        const charityEvents = response.data.filter(event => {
          const hasCharityTag = event.tags && event.tags.includes('charity');
          const isUpcoming = event.status === 'upcoming' || new Date(event.date) >= new Date();
          return hasCharityTag && isUpcoming;
        });
        setUpcomingEvents(charityEvents);
      }
    } catch (error) {
      console.warn('Could not fetch charity events:', error);
      // Keep empty array as default
      setUpcomingEvents([]);
    }
  };

  if (loading) {
    return (
      <div style={{minHeight: '100vh'}}>
        <div className="container text-center" style={{padding: '4rem 1rem'}}>
          <div className="spinner"></div>
          <p style={{color: 'var(--forest-600)', marginTop: '1rem'}}>Loading charity information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{minHeight: '100vh'}}>
        <div className="container text-center" style={{padding: '4rem 1rem'}}>
          <p style={{color: 'var(--earth-600)', marginBottom: '1rem'}}>⚠️ {error}</p>
          <button onClick={fetchCharityData} className="btn btn-outline">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Calculate dynamic statistics from charity data
  const totalRaised = charities.reduce((sum, charity) => sum + (charity.amountRaised || 0), 0);
  const totalCharities = charities.length;

  return (
    <div style={{minHeight: '100vh'}}>
      <div className="container" style={{padding: '4rem 1rem'}}>
        {/* Page Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-6">
            Charity & Giving Back
          </h1>
          <p className="text-lg text-forest-600 max-w-3xl mx-auto">
            Charity is a cornerstone of The Canopy. We believe in using our platform and 
            community to make a positive impact in the world. Through gaming marathons, 
            special events, and ongoing campaigns, we support causes close to our hearts.
          </p>
        </div>

        {/* Impact Statistics */}
        <div className="grid grid-cols-3 gap-4 mb-16 text-center">
          <div className="card" style={{padding: '1rem'}}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--gold-600)', marginBottom: '0.5rem'}}>
              ${totalRaised.toLocaleString()}+
            </div>
            <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Total Raised</div>
          </div>
          <div className="card" style={{padding: '1rem'}}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--forest-600)', marginBottom: '0.5rem'}}>
              {statisticsData.charity?.totalCharityGoal || '$10,000'}
            </div>
            <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Charity Goal</div>
          </div>
          <div className="card" style={{padding: '1rem'}}>
            <div style={{fontSize: '2rem', fontWeight: 'bold', color: 'var(--earth-600)', marginBottom: '0.5rem'}}>
              {statisticsData.charity?.nextEventGoal || 'TBA'}
            </div>
            <div style={{color: 'var(--forest-800)', fontWeight: '500'}}>Next Event Goal</div>
          </div>
        </div>

        {/* Hero Message */}
        <div className="card text-center mb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gradient mb-6">
              Making a Difference Through Gaming
            </h2>
            <p className="text-forest-600 mb-6 text-lg">
              Every stream, every viewer, every donation matters. Together with our amazing 
              community, we've been able to support incredible organizations that are making 
              real change in the world. Gaming isn't just entertainment—it's a force for good.
            </p>
            <div style={{display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap'}}>
              {statisticsData.charity?.donationLink && (
                <a 
                  href={statisticsData.charity.donationLink} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-enhanced btn-primary"
                >
                  Donate to Our Current Cause
                </a>
              )}
              <a 
                href="https://discord.com/channels/1383056994570862604/1383056995417980970" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-enhanced btn-outline"
                style={{ textDecoration: 'none', display: 'inline-block' }}
              >
                Suggest a Charity
              </a>
            </div>
          </div>
        </div>

        {/* Supported Charities */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gradient mb-8 text-center">
            Our Charity Partners
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {charities.map((charity, index) => (
              <CharityCard key={index} charity={charity} />
            ))}
          </div>
        </div>

        {/* Upcoming Charity Events */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gradient mb-8 text-center">
            Upcoming Charity Events
          </h2>
          <div className="space-y-6">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.map((event, index) => (
                <div key={index} className="event-timeline card">
                  <div className="event-dot"></div>
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                    <div className="flex-1 mb-4 md:mb-0">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-medium text-earth-600 bg-earth-100 px-3 py-1 rounded-full">
                          {new Date(event.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                          {event.time && ` at ${event.time}`}
                        </span>
                        <span className="text-sm font-medium text-forest-600 bg-forest-100 px-3 py-1 rounded-full">
                          {event.type || 'Charity Event'}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-forest-800 mb-2">
                        {event.title}
                      </h3>
                      <p className="text-forest-600">
                        {event.description}
                      </p>
                      {event.host && (
                        <p className="text-sm text-forest-500 mt-2">
                          Hosted by: {event.host}
                        </p>
                      )}
                    </div>
                    <div className="flex-shrink-0">
                      {event.link ? (
                        <a 
                          href={event.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-outline text-sm"
                        >
                          Learn More
                        </a>
                      ) : (
                        <span className="text-sm text-forest-500">More details coming soon</span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-forest-600 text-lg mb-4">
                  🌱 {statisticsData.charity?.eventDescription || 'No upcoming charity events scheduled at the moment.'}
                </p>
                <p className="text-forest-500">
                  Check back soon for new charity initiatives and community events!
                </p>
              </div>
            )}
          </div>
        </div>

        {/* How We Choose Charities */}
        <div className="mb-16">
          <div className="card">
            <h2 className="text-2xl font-bold text-gradient mb-6 text-center">
              How We Choose Our Causes
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-forest-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">🎯</span>
                </div>
                <h3 className="text-lg font-bold text-forest-800 mb-2">
                  Mission Alignment
                </h3>
                <p className="text-forest-600 text-sm">
                  We support causes that align with our values of community, inclusion, and positive impact.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gold-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">🔍</span>
                </div>
                <h3 className="text-lg font-bold text-forest-800 mb-2">
                  Transparency
                </h3>
                <p className="text-forest-600 text-sm">
                  We research and verify that charities use donations effectively and transparently.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-earth-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-lg font-bold text-forest-800 mb-2">
                  Community Input
                </h3>
                <p className="text-forest-600 text-sm">
                  Our community suggests causes, and we vote together on which charities to support.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Get Involved */}
        <div className="card text-center">
          <h2 className="text-2xl font-bold text-gradient mb-4">
            Get Involved
          </h2>
          <p className="text-forest-600 mb-6">
            Want to help make a difference? There are many ways to get involved with our charitable efforts.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 border border-forest-200 rounded-lg">
              <h3 className="font-semibold text-forest-800 mb-2">Donate</h3>
              <p className="text-sm text-forest-600">
                Contribute directly to our current charity campaigns during streams.
              </p>
            </div>
            <div className="p-4 border border-forest-200 rounded-lg">
              <h3 className="font-semibold text-forest-800 mb-2">Participate</h3>
              <p className="text-sm text-forest-600">
                Join our charity streams and help us reach our fundraising goals.
              </p>
            </div>
            <div className="p-4 border border-forest-200 rounded-lg">
              <h3 className="font-semibold text-forest-800 mb-2">Spread the Word</h3>
              <p className="text-sm text-forest-600">
                Share our charity events on social media to expand our reach.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Charity;